import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  finalizeAllianceTaskPeriod,
  getTechTree,
  type AllianceTaskPeriodClient,
  type TechNode,
} from '../../common/allianceApi'
import { ALLIANCE_TASK_TITLE_STYLE } from '../../common/allianceTaskOptionsClient'
import { alert } from '../Alert/Alert'
import {
  TASK_CINZEL,
  TASK_TEXT_BG,
  TaskFooter,
  TaskModalShell,
  taskFieldStyle,
  taskLabelStyle,
} from './allianceTaskModalKit'

interface AllianceTechTargetModalProps {
  open: boolean
  address: string
  period: AllianceTaskPeriodClient
  onBack: () => void
  onFinalized: () => void
}

const AT = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/alliance/alliance-task'
const MODAL_BG = `${AT}/modal-bg.webp`
const TEXT_BG = `${AT}/text-bg.webp`
const CONFIRM_BTN = `${AT}/confirm-btn.webp`
const CINZEL = '"Cinzel", "Trajan Pro", "Times New Roman", serif'

/** Small label above each field (uppercase antique gold, matches the game modal art). */
const labelStyle: React.CSSProperties = {
  fontFamily: CINZEL,
  fontWeight: 700,
  fontSize: '2.2cqw',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#5a3c17',
}

/** Small gear glyph for each technology row. */
const GearGlyph: React.FC = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3.2" />
    <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.2 5.2l2.1 2.1M16.7 16.7l2.1 2.1M18.8 5.2l-2.1 2.1M7.3 16.7l-2.1 2.1" />
  </svg>
)

/**
 * Technology target modal — shown after the leader picks the Technology official task.
 * Pick an alliance tech building and how many times to advance it this season. Governance
 * follows the vote-rank tier (no custom bonus). No cancel/close — a target must be chosen
 * to finalize the season. Styled to match the game's alliance-task modal art.
 */
export const AllianceTechTargetModal: React.FC<AllianceTechTargetModalProps> = ({
  open,
  address,
  period,
  onBack,
  onFinalized,
}) => {
  const { t } = useTranslation()
  const [buildings, setBuildings] = useState<TechNode[]>([])
  const [levels, setLevels] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [buildingId, setBuildingId] = useState('')
  const [times, setTimes] = useState(1)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open || !address) return
    let alive = true
    setLoading(true)
    void getTechTree(address)
      .then((r) => {
        if (!alive) return
        const map = r.techBuildingLevels || {}
        const list = r.buildings && r.buildings.length ? r.buildings : Object.values(r.techTree || {})
        // Use the authoritative per-building level map as the current level (fall back to node level).
        const merged = list.map((b) => ({ ...b, level: map[b.id] ?? b.level ?? 0 }))
        setLevels(map)
        setBuildings([...merged].sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0)))
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [open, address])

  const selectedBuilding = useMemo(
    () => buildings.find((b) => b.id === buildingId) || null,
    [buildings, buildingId],
  )
  const maxLevel = selectedBuilding?.maxLevel ?? 100
  // Current alliance level of the picked building (authoritative map first).
  const currentLevel = buildingId ? levels[buildingId] ?? selectedBuilding?.level ?? 0 : 0
  const maxTimes = Math.max(1, maxLevel - currentLevel)
  const clampTimes = (v: number) => Math.max(1, Math.min(maxTimes, Math.floor(v) || 1))
  const targetLevel = Math.min(maxLevel, currentLevel + clampTimes(times))

  if (!open || typeof document === 'undefined') return null

  const handleConfirm = async () => {
    if (!buildingId || busy) return
    const advance = clampTimes(times)
    setBusy(true)
    try {
      const res = await finalizeAllianceTaskPeriod(address, 'technology', period._id, {
        techBuildingId: buildingId,
        techAdvanceTimes: advance,
      })
      const delta = res.governanceDelta ?? 0
      alert.open(
        t('alliance.taskFinalized', {
          defaultValue: 'Official task locked. Governance {{delta}}.',
          delta: delta > 0 ? `+${delta}` : String(delta),
        }),
      )
      onFinalized()
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      alert.open(e?.response?.data?.message || e?.message || t('alliance.taskFinalizeFailed', { defaultValue: 'Could not lock the task.' }))
      setBusy(false)
    }
  }

  return (
    <TaskModalShell
      title={t('alliance.techTargetTitle', { defaultValue: 'Set Technology Target' })}
      subtitle={t('alliance.techTargetSubtitle', { defaultValue: 'Advance an alliance technology this season.' })}
      width="min(94vw, 560px)"
      padding="5% 5% 4%"
    >
      {loading ? (
        <p className="py-[6cqw] text-center" style={{ fontFamily: TASK_CINZEL, fontSize: '3cqw', color: 'rgba(74,52,24,0.75)' }}>
          {t('common.loading', { defaultValue: 'Loading...' })}
        </p>
      ) : (
        <div className="flex flex-col" style={{ gap: '3.5cqw' }}>
          <div className="flex flex-col" style={{ gap: '1.2cqw' }}>
            <span style={taskLabelStyle}>{t('alliance.techBuilding', { defaultValue: 'Technology' })}</span>
            <select
              value={buildingId}
              onChange={(e) => {
                setBuildingId(e.target.value)
                setTimes(1)
              }}
              disabled={busy}
              style={{ ...taskFieldStyle, colorScheme: 'dark' }}
            >
              <option value="">{t('alliance.techPickBuilding', { defaultValue: 'Choose a building…' })}</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.shortLabel || b.name} (Lv {levels[b.id] ?? b.level ?? 0})
                </option>
              ))}
            </select>
          </div>

          {selectedBuilding ? (
            <div
              className="flex items-center"
              style={{
                height: '10cqw',
                padding: '0 4cqw',
                backgroundImage: `url(${TASK_TEXT_BG})`,
                backgroundSize: '100% 100%',
                backgroundRepeat: 'no-repeat',
                fontFamily: TASK_CINZEL,
                fontWeight: 700,
                fontSize: '2.8cqw',
                color: '#f3ead0',
              }}
            >
              {t('alliance.techCurrentLevel', { defaultValue: 'Current level: {{lvl}} / {{max}}', lvl: currentLevel, max: maxLevel })}
            </div>
          ) : null}

          <div className="flex flex-col" style={{ gap: '1.2cqw' }}>
            <span style={taskLabelStyle}>{t('alliance.techAdvanceTimes', { defaultValue: 'Advance times' })}</span>
            <input
              type="number"
              min={1}
              max={maxTimes}
              value={times}
              onChange={(e) => setTimes(Number(e.target.value))}
              disabled={busy || !buildingId}
              style={{ ...taskFieldStyle, colorScheme: 'dark' }}
            />
            {selectedBuilding ? (
              <span style={{ fontFamily: TASK_CINZEL, fontSize: '2.2cqw', color: '#8a6a3c' }}>
                {t('alliance.techTargetLevel', {
                  defaultValue: 'Target: Lv {{cur}} → Lv {{lvl}} (max {{max}})',
                  cur: currentLevel,
                  lvl: Math.min(maxLevel, currentLevel + Math.max(1, Math.min(maxTimes, Math.floor(times) || 1))),
                  max: maxLevel,
                })}
              </span>
            ) : null}
          </div>
        </div>
      )}

      <TaskFooter
        onBack={onBack}
        backLabel={t('common.back', { defaultValue: 'Back' })}
        backDisabled={busy}
        onConfirm={() => void handleConfirm()}
        confirmDisabled={!buildingId || busy}
        confirmLabel={busy ? t('common.loading', { defaultValue: 'Loading...' }) : t('alliance.techTargetConfirm', { defaultValue: 'Confirm Technology' })}
      />
    </TaskModalShell>
  )
}

/** Square parchment +/- button for the advance-times stepper. */
const StepperButton: React.FC<{ label: string; disabled?: boolean; onClick: () => void }> = ({ label, disabled, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="flex shrink-0 items-center justify-center transition-[filter,transform] hover:brightness-110 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-40"
    style={{
      width: '10cqw',
      height: '10cqw',
      backgroundImage: `url(${TEXT_BG})`,
      backgroundSize: '100% 100%',
      backgroundRepeat: 'no-repeat',
      fontFamily: CINZEL,
      fontWeight: 800,
      fontSize: '4.4cqw',
      lineHeight: 1,
      color: '#f0d488',
      textShadow: '0 1px 2px rgba(30,15,4,0.9)',
    }}
  >
    {label}
  </button>
)

export default AllianceTechTargetModal
